import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Calendar, Clock, MapPin, IndianRupee, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UpcomingTrainings = () => {
  const [selectedMode, setSelectedMode] = useState('All');
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const navigate = useNavigate();
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const modes = ['All', 'Classroom', 'Online', 'Hybrid', 'On-Site'];

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/training-calendar`, {
        params: { limit: 6 }
      });
      setTrainings(response.data || []);
    } catch (err) {
      console.error('Error fetching trainings:', err);
      setError('Failed to load training programs');
    } finally {
      setLoading(false);
    }
  };

  const filteredTrainings = selectedMode === 'All'
    ? trainings
    : trainings.filter(training => training.mode === selectedMode);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section id="upcoming" className="py-20 bg-white" ref={ref}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 space-y-4"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Upcoming <span className="text-blue-600">Training Programs</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Book your training online today. Transform your career prospects with in-demand skills and qualifications.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {modes.map((mode, index) => (
            <motion.button
              key={mode}
              onClick={() => setSelectedMode(mode)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                selectedMode === mode
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              {mode}
            </motion.button>
          ))}
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-10">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchTrainings} variant="outline">
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredTrainings.length === 0 && (
          <div className="text-center py-10">
            <p className="text-gray-600 text-lg">
              {selectedMode === 'All'
                ? 'No upcoming trainings available at the moment.'
                : `No ${selectedMode} trainings available.`}
            </p>
            <Button
              onClick={() => navigate('/training-calendar')}
              className="mt-4"
            >
              View Training Calendar
            </Button>
          </div>
        )}

        {/* Trainings Grid - Updated to match PopularCourses */}
        {!loading && !error && filteredTrainings.length > 0 && (
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            key={selectedMode}
          >
            {filteredTrainings.map((training) => (
              <motion.div key={training.id} variants={cardVariants}>
                <Card
                  className="group h-full hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-200 cursor-pointer"
                  onClick={() => training.slug && navigate(`/training-event/${training.slug}`)}
                >
                  {/* Poster Image (if available) - Same as PopularCourses */}
                  {training.poster_url && (
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img
                        src={training.poster_url.startsWith('http') ? training.poster_url : `${BACKEND_URL}${training.poster_url}`}
                        alt={training.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.display = 'none';
                        }}
                      />
                      {training.is_featured && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-yellow-500 text-white">
                            Featured
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}

                  <CardHeader>
                    <div className="flex justify-between items-start mb-3">
                      <Badge className="bg-blue-100 text-blue-700">
                        {training.mode || 'Training'}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`border-green-300 ${
                          training.seats_available > 0
                            ? 'text-green-700'
                            : 'text-red-700 border-red-300'
                        }`}
                      >
                        {training.seats_available > 0
                          ? `${training.seats_available} seats`
                          : 'Fully Booked'}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight line-clamp-2">
                      {training.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <span>{formatDate(training.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span>{training.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-blue-600" />
                        <span>{training.location}</span>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                      {training.is_paid ? (
                        <div className="flex items-center gap-1">
                          <IndianRupee className="h-5 w-5 text-blue-600" />
                          <span className="text-2xl font-bold text-blue-600">
                            {training.price?.replace('₹', '')}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xl font-bold text-green-600">Complimentary</span>
                      )}
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/training-event/${training.slug}`);
                          }}
                          disabled={training.seats_available <= 0}
                        >
                          {training.seats_available > 0 ? 'Register' : 'Fully Booked'}
                        </Button>
                      </motion.div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* View All Button */}
        {!loading && !error && trainings.length > 0 && (
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg transition-all"
                onClick={() => navigate('/training-calendar')}
              >
                View Full Training Calendar
              </Button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
